/**
 * RETORNO AO CANDIDATO — o que a tela e o servidor precisam dizer IGUAL.
 *
 * Fechado o processo seletivo, o RH devolve o resultado pelo WhatsApp: o
 * retorno POSITIVO (aprovado, com a proposta — vaga, salário, horário e
 * benefícios) ou o NEGATIVO (o agradecimento a quem não seguiu). Os dois textos
 * foram passados pelo DP/RH em 22/10/2026 e estão abaixo como vieram, só com
 * os marcadores no lugar de "[Nome]", "(nome da vaga)" e dos "deixar aberto".
 *
 * Eles são o PADRÃO DE FÁBRICA. O RH ajusta o modelo na própria tela ("Editar
 * o modelo") e o que ficar gravado em `rh_config` passa por cima daqui — mudou
 * um benefício, não se mexe neste arquivo nem se espera atualização do sistema.
 *
 * Arquivo SEM imports de propósito (mesmo padrão de src/roteiro-entrevista.ts):
 * o servidor (server/retorno-entrevista.ts) valida contra estas regras e a tela
 * monta o texto com elas. Duas cópias acabariam aceitando coisas diferentes.
 */

export type TipoRetorno = "positivo" | "negativo";
export const TIPOS_RETORNO: TipoRetorno[] = ["positivo", "negativo"];

export const ROTULO_TIPO: Record<TipoRetorno, string> = {
  positivo: "Retorno positivo",
  negativo: "Retorno negativo",
};

/* ------------------------------------------------------------------ *
 *  Os modelos de fábrica                                               *
 * ------------------------------------------------------------------ */

/**
 * O texto do RH, linha por linha. Duas adaptações só de forma: o "•" + TAB que
 * veio da lista do Word virou "• " (o TAB chega torto no WhatsApp) e os espaços
 * soltos no fim das linhas saíram.
 */
export const MODELOS_ORIGINAIS: Record<TipoRetorno, string> = {
  negativo: [
    "Olá, {nome}! Tudo bem?",
    "Agradecemos muito pela sua participação em nosso processo seletivo e pelo tempo dedicado às etapas.",
    "Após avaliarmos seu perfil em conjunto com os demais candidatos, neste momento decidimos seguir com outro profissional que apresentou maior alinhamento com os requisitos e necessidades atuais da posição.",
    "Agradecemos seu interesse em fazer parte da nossa equipe e desejamos muito sucesso na sua trajetória profissional.",
    "Seu perfil poderá ser considerado em futuras oportunidades que tenham maior aderência às suas experiências.",
    "Obrigado pela participação e boa sorte nos seus próximos desafios!",
  ].join("\n"),
  positivo: [
    "Olá {nome}, tudo bem?",
    "Gostaríamos muito de agradecer o interesse, disponibilidade e tempo investidos no processo seletivo para fazer parte da nossa equipe.",
    "Acreditamos que você está alinhado com o que procuramos e com os nossos valores.",
    "Portanto, PARABÉNS 🎉! Você está aprovado(a) para a vaga de {vaga}!",
    "Segue abaixo a nossa proposta de trabalho, caso seja positivo, você receberá um contato do nosso Departamento Pessoal para acertar os detalhes da contratação e envio dos documentos necessários.",
    "Vaga: {vaga}",
    "Salário: {salario}",
    "Horário: {horario}",
    "Benefícios:",
    "• Vale transporte em cartão mobilidade;",
    "• Refeição oferecido pela empresa (café da manhã, almoço e café da tarde);",
    "• Incentivo faculdade;",
    "• Seguro de vida;",
    "• Plano de cargos e salários;",
    "• Convênio Farmácia;",
    "• Gympass;",
    "• Telemedicina;",
    "• Programas de Incentivos Interno.",
  ].join("\n"),
};

/** Mensagem e modelo: bem mais que os dois textos, bem menos que um abuso. */
export const MAX_MENSAGEM_RETORNO = 4000;
export const MAX_MODELO_RETORNO = 4000;

/* ------------------------------------------------------------------ *
 *  Marcadores                                                          *
 * ------------------------------------------------------------------ */

export type Marcador = "nome" | "vaga" | "salario" | "horario";

export const MARCADORES: { chave: Marcador; rotulo: string }[] = [
  { chave: "nome", rotulo: "nome do candidato" },
  { chave: "vaga", rotulo: "vaga" },
  { chave: "salario", rotulo: "salário" },
  { chave: "horario", rotulo: "horário" },
];

export const ROTULO_MARCADOR: Record<Marcador, string> = Object.fromEntries(
  MARCADORES.map((m) => [m.chave, m.rotulo]),
) as Record<Marcador, string>;

/**
 * "{salario}", "{Salário}", "{ horario }" — quem edita o modelo escreve com e
 * sem acento, e um marcador que não é reconhecido iria para o candidato do
 * jeito que está. (Global: `replace` e `matchAll` não dependem do lastIndex.)
 */
const PADRAO_MARCADOR = /\{\s*(nome|vaga|sal[aá]rio|hor[aá]rio)\s*\}/gi;

function chaveDoMarcador(bruto: string): Marcador {
  return bruto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase() as Marcador;
}

/** Os marcadores que aparecem no texto, na ordem da lista (sem repetir). */
export function marcadoresDoTexto(texto: string): Marcador[] {
  const achados = new Set<Marcador>();
  for (const m of String(texto ?? "").matchAll(PADRAO_MARCADOR)) {
    achados.add(chaveDoMarcador(m[1]!));
  }
  return MARCADORES.map((m) => m.chave).filter((c) => achados.has(c));
}

/**
 * Troca os marcadores pelos valores. Valor vazio deixa o marcador NO TEXTO —
 * é assim que a prévia mostra o que ainda falta, e o envio é barrado enquanto
 * sobrar algum (ver `marcadoresPendentes`).
 */
export function aplicarModelo(modelo: string, valores: Partial<Record<Marcador, string>>): string {
  return String(modelo ?? "").replace(PADRAO_MARCADOR, (inteiro, bruto: string) => {
    const valor = String(valores[chaveDoMarcador(bruto)] ?? "").trim();
    return valor || inteiro;
  });
}

/** O que ainda está por preencher na mensagem (a lista vazia libera o envio). */
export function marcadoresPendentes(texto: string): Marcador[] {
  return marcadoresDoTexto(texto);
}

/* ------------------------------------------------------------------ *
 *  Quem recebe                                                         *
 * ------------------------------------------------------------------ */

/** Primeiro nome, para o texto não começar com o nome inteiro em caixa alta. */
export function primeiroNome(nome: string | null | undefined): string {
  const parte = String(nome ?? "").trim().split(/\s+/)[0] ?? "";
  if (!parte) return "";
  return parte.charAt(0).toUpperCase() + parte.slice(1).toLowerCase();
}

/**
 * Telefone como o candidato escreveu → número do WhatsApp (só dígitos, com o
 * 55). Até 11 dígitos é número brasileiro sem o código do país; o zero da
 * frente (o jeito antigo de discar o DDD) sai antes. Sem dígito nenhum = "".
 */
export function numeroWhatsApp(telefone: string | null | undefined): string {
  const n = String(telefone ?? "").replace(/\D/g, "").replace(/^0+/, "");
  if (!n) return "";
  return n.length <= 11 ? `55${n}` : n;
}

/**
 * Número utilizável: com DDD (55 + 10 ou 11 dígitos) ou internacional completo.
 * Celular digitado sem o DDD vira 55 + 9 dígitos = 11 — curto demais, e o
 * WhatsApp abriria a conversa de outra pessoa ou de ninguém.
 */
export function telefoneValido(numero: string): boolean {
  return /^\d{12,15}$/.test(numero);
}

/** "5519999998888" → "(19) 99999-8888"; fora do Brasil, "+<dígitos>". */
export function formatarTelefone(numero: string | null | undefined): string {
  const n = String(numero ?? "").replace(/\D/g, "");
  if (!n) return "";
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(n);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : `+${n}`;
}

/**
 * O link que abre a conversa com o texto pronto.
 *
 * É `api.whatsapp.com/send` e NÃO `wa.me` de propósito — testado em 22/10/2026:
 * o redirecionamento do wa.me troca emoji (caracteres de 4 bytes, como o 🎉 do
 * retorno positivo) por "�" (`%F0%9F%8E%89` vira `%EF%BF%BD` no Location),
 * enquanto o api.whatsapp.com repassa o texto intacto ao WhatsApp Web e ao
 * aplicativo. Sem número, o WhatsApp abre para o RH escolher a conversa.
 */
export function linkWhatsAppRetorno(numero: string, texto: string): string {
  const fone = numero ? `phone=${numero}&` : "";
  return `https://api.whatsapp.com/send?${fone}text=${encodeURIComponent(texto)}`;
}

/* ------------------------------------------------------------------ *
 *  O status da entrevista                                              *
 * ------------------------------------------------------------------ */

/** O retorno que combina com o status ("Aprovado 1º lugar" → positivo). */
export function tipoPorStatus(status: string | null | undefined): TipoRetorno | null {
  const s = String(status ?? "").trim();
  if (s.startsWith("Aprovado")) return "positivo";
  if (s === "Reprovado") return "negativo";
  return null;
}

/**
 * O retorno escolhido contradiz o status? Mandar "PARABÉNS" para quem foi
 * reprovado é o erro que não tem volta — a tela pergunta antes de abrir o
 * WhatsApp. Não trava: o status pode estar desatualizado.
 */
export function avisoDeStatus(tipo: TipoRetorno, status: string | null | undefined): string | null {
  const s = String(status ?? "").trim();
  if (!s) return null;
  if (tipo === "positivo" && ["Reprovado", "Desistiu", "Não compareceu"].includes(s)) {
    return `O status desta entrevista é "${s}" e este é o retorno POSITIVO (aprovação).`;
  }
  if (tipo === "negativo" && s.startsWith("Aprovado")) {
    return `O status desta entrevista é "${s}" e este é o retorno NEGATIVO.`;
  }
  return null;
}
